import { Component, HostListener, Inject, Injector, Input, OnDestroy, ViewChild } from '@angular/core';
import { APP_BASE_HREF } from '@angular/common';

import { MessageService } from 'primeng/api';
import { forkJoin, defer, Observable, of, Subscription } from 'rxjs';
import { map, mergeMap, takeUntil, switchMap } from 'rxjs/operators';

import { AIApi } from '@api/ai/AI-api';
import { ViAnswerRequestApi } from '@api/visibilityIntelligence/models/vi-answer-request.api.model';
import { JournodexSearchesApi } from '@api/targeting/journodex-searches.api';
import { OutletsPublicApi } from '@api/targeting/outlets-public.api';
import { SearchContactsAndOutletsTypeApiEnum } from '@api/targeting/enum/search-contacts-and-outlets-type.enum';

import { AbstractDialogComponent } from '@Core/Dialogs/components/AbstractDialog/abstract-dialog.component';
import { AnswerEngineTypesEnum } from '../../enums/answer-engine-types.enum';
import { ButtonTypesEnum } from '@Common/Forms/enums/button-types.enum';
import { ConfirmDialogComponent } from '@Common/Dialogs/components/ConfirmDialog/confirm-dialog.component';
import { ConfirmDialogModel } from '@Common/Dialogs/models/confirm-dialog.model';

import { CoreDialogService } from '@Core/Dialogs/services/dialog.service';
import { CurrentUserStateService } from '@Core/Users/services/current-user-state.service';
import { FullProfileAnimationModel } from '@Core/Dialogs/models/full-profile-animation.model';
import { HubspotFormService } from '@Common/Misc/services/hubspot-form.service';
import { HubspotFormTypeEnum } from '@Common/Misc/enums/hubspot-form-type.enum';
import { LocalizationService } from '@Core/Localization/services/localization.service';
import { messageSeverityEnum } from '@Common/Misc/enums/message-severity.enum';

import { TakeOverPanelComponent } from '@Common/TakeOver/components/TakeOverPanel/take-over-panel.component';
import { userPermissionsEnum } from '@Core/Users/enums/user-permissions.enum';


import { CirculationModel } from '@Discover/Targeting/models/circulation.model';
import { OutletModel } from '@Discover/Targeting/models/outlet.model';

import { BrandIdentityService } from '@Common/BrandIdentity/services/brand-identity.service';
import { BrandIdentity } from '@Common/BrandIdentity/models/brand-identity';

import { ViAnalysisEnum } from '../../enums/vi-analysis.enum';

import { ViInsightPrompt } from '../../models/vi-insight-prompt.model';
import { ViAnswerEngineSource } from '../../models/vi-answer-engine-source.model';
import { AnswerEngineOption, getAllEngineOptions } from '../../models/answer-engine-option.model';

import { copy } from '@Common/Misc/helpers/copy/copy.helper';

import { parseTextToHtml } from '../../helpers/vi-html.helper';

import { htmlToFormattedText } from '@Common/Misc/helpers/html/html.helper';
import { ViSourceOutlet } from '../../models/vi-source-outlet.model';
import { answerEngineTypesToModelIdMap } from '../../models/answer-engine-model-id.map';
import { ViApiService } from '@api/visibilityIntelligence/vi.api.service';
import { ViAnswerApi } from '@api/visibilityIntelligence/models/vi-answer.api.model';
import { isBlockedHost } from '../../helpers/vi-outlet-url-block-list';


interface ViAnswerResponse {
    text: string;
    answerEngine: AnswerEngineTypesEnum;
    citations: ViAnswerEngineSource[];
}


@Component({
    selector: 'agpr-vi-agent-dialog',
    templateUrl: './vi-agent-dialog.component.html',
    styleUrls: ['./vi-agent-dialog.component.scss'],
    providers: [
        JournodexSearchesApi,
    ],
    standalone: false
})
export class ViAgentDialogComponent extends AbstractDialogComponent implements OnDestroy {

    @Input() overlayContainer: Element;
    @Input() animationTrigger: Observable<FullProfileAnimationModel>;

    @ViewChild('takeOverPanel') takeOverPanel: TakeOverPanelComponent;

    viewportHeight: number;
    hideSlideOverlay: boolean = false;
    isLandingPage: boolean = true;

    isProvisioned: boolean = false;
    isAnswerGenerating: boolean = false;

    answerEngineOptions: AnswerEngineOption[] = getAllEngineOptions();
    selectedEngineType: AnswerEngineTypesEnum = this.answerEngineOptions[0].type;

    executedPrompt: string;
    prompt: string = '';

    selectedLanguage: string = '';
    selectedBrand: BrandIdentity;

    outletList: ViSourceOutlet[] = [];
    outletListLoading: boolean = false;

    subscriptions: Subscription[] = [];

    readonly id = 'vi-agentDialog';
    readonly STANDARD_HEIGHT = 684;
    readonly messageSeverityEnum = messageSeverityEnum;
    readonly closeValidation$: Observable<boolean> = defer(() => this.isLandingPage ? of(true) : this.confirmClose());
    readonly buttonTypes = ButtonTypesEnum;
    readonly messageSeverities = messageSeverityEnum;


    readonly prompts: ViInsightPrompt[] = [
        {
            analysisType: ViAnalysisEnum.SENTIMENT_ANALYSIS,
            expanded: false,
            aiResponse: ''
        },
        {
            analysisType: ViAnalysisEnum.REPRESENTATION_ANALYSIS,
            expanded: false,
            aiResponse: ''
        },
        {
            analysisType: ViAnalysisEnum.COMPETITIVE_ANALYSIS,
            expanded: false,
            aiResponse: ''
        },
        {
            analysisType: ViAnalysisEnum.CITATION_AUTHORITY_ANALYSIS,
            expanded: false,
            aiResponse: ''
        },
        {
            analysisType: ViAnalysisEnum.CUSTOM,
            isCustomPrompt: true,
            expanded: false,
            aiResponse: ''
        }
    ];


    constructor(
        public coreDialogService: CoreDialogService,
        public currentUserStateService: CurrentUserStateService,
        public hubspotFormService: HubspotFormService,
        public localizationService: LocalizationService,
        public messageService: MessageService,
        public journodexSearchesApi: JournodexSearchesApi,
        public outletsPublicApi: OutletsPublicApi,
        public brandIdentityService: BrandIdentityService,
        public injector: Injector,
        @Inject(APP_BASE_HREF) public appBaseHref: string,
        private viApiService: ViApiService,
        private aiApiService: AIApi
    ) {
        super(coreDialogService, 'ViAgentDialog');
        this.viewportHeight = window.innerHeight;

        this.subscriptions.push(
            this.currentUserStateService.getPermissions().subscribe(permissions => {
                if (permissions) {
                    const requiredPermissions = [
                        userPermissionsEnum.openAI,
                        userPermissionsEnum.visibilityIntelligence,
                    ];

                    this.isProvisioned = requiredPermissions.every(p => permissions.includes(p));
                }
            }),
        );
    }

    @HostListener('window:resize') onResize() {
        this.viewportHeight = window.innerHeight;
    }


    cancel() {
        this.prompts.forEach(prompt => {
            if (prompt.destroyApiCall$) {
                prompt.destroyApiCall$.next();
            }
            if (prompt.apiCall) {
                prompt.apiCall.unsubscribe();
            }
            prompt.generating = false;
        });

        this.hideSlideOverlay = true;
        this.cancelGetAnswers();
    }

    close(overlayVisibility: boolean) {
        if (!overlayVisibility) {
            this.closeDialog();
        }
    }

    requestClose() {
        this.takeOverPanel?.close();
    }


    getAnswers() {
        this.isLandingPage = false;
        this.selectedEngineType = null;

        this.outletListLoading = true;
        this.isAnswerGenerating = true;

        this.prompt = (this.prompt || this.executedPrompt)
            .replace(/<[^>]*>/g, '') // remove html tag
            .trim();

        this.executedPrompt = this.prompt;

        // Insight initialization
        this.prompts.forEach(prompt => {
            if (prompt.destroyApiCall$) {
                prompt.destroyApiCall$.next();
            }
            if (prompt.apiCall) {
                prompt.apiCall.unsubscribe();
            }
            prompt.aiResponse = '';
            prompt.generating = false;
            prompt.responseHtml = '';
            prompt.expanded = false;
        });

        this.answerEngineOptions.forEach(engineType => {
            engineType.loading = true;
            engineType.answerText = '';
            engineType.sources = [];

            const request: ViAnswerRequestApi = {
                userMessage: this.prompt,
                modelConfig: {
                    modelId: answerEngineTypesToModelIdMap.get(engineType.type) as string,
                    webSearch: true,
                }
            };
            this.getAnswersEngineResults(request, engineType);
        });
    }

    cancelGetAnswers() {
        this.answerEngineOptions.forEach((engine: AnswerEngineOption) => {
            engine.destroyApiCall$.next();
            engine.loading = false;
        });
        this.getAnswersEngineResultCheck();
    }

    private getAnswerEngineResult(request: ViAnswerRequestApi, option: AnswerEngineOption): Observable<ViAnswerResponse> {
        return this.viApiService.getViAnswer(request).pipe(
            switchMap((response) => {
                const entity: ViAnswerApi = response;
                option.rawResponse = JSON.parse(JSON.stringify(entity));

                const redirectUrls = entity.citations
                    .map(x => x.url)
                    .filter(url => url?.includes('vertexaisearch.cloud.google.com/grounding-api-redirect/'));

                const resolveRedirects$: Observable<string[]> = redirectUrls.length > 0
                    ? this.aiApiService.resolveRedirects(redirectUrls)
                    : of([]);

                return resolveRedirects$.pipe(
                    map((resolvedUrls) => {
                        const redirectMap = new Map<string, string>(
                            redirectUrls.map((url, i) => [url, resolvedUrls[i]] as const)
                        );

                        let html: string = entity.text;
                        try {
                            html = JSON.parse(html).html;
                        } catch (e) {
                            // Ignore it, sometimes it's just a string
                        }
                        html = this.cleanHtml(html);
                        return {
                            text: html,
                            // @HACK - since backend returns display string in entity.answerEngine,
                            // use the engine we requested rather
                            answerEngine: option.type,
                            citations: entity.citations.map(x => {
                                return {
                                    link: redirectMap.get(x.url) ?? x.url,
                                    title: x.title,
                                    contactName: x.author,
                                    outletIconUrl: x.icon
                                } as ViAnswerEngineSource;
                            })
                        } as ViAnswerResponse;
                    })
                );
            })
        );
    }

    private getAnswersEngineResults(request: ViAnswerRequestApi, engineOption: AnswerEngineOption) {
        let answer: string = '';

        this.getAnswerEngineResult(request, engineOption).pipe(
            takeUntil(engineOption.destroyApiCall$),
            mergeMap((response: ViAnswerResponse) => {
                answer = response.text;
                const items = response.citations;
                if (!items.length) {
                    return of([]);
                }
                return forkJoin(
                    items.map(item => {
                        if (!item.link) {
                            return of(null);
                        }
                        return this.findMatchingOutlet(item).pipe(
                            takeUntil(engineOption.destroyApiCall$),
                            mergeMap(source => {
                                if (source.outletId) {
                                    return this.outletsPublicApi.getOutlet(source.outletId);
                                } else {
                                    return of(null);
                                }
                            }),
                            map(outlet => {
                                if (outlet) {
                                    item.outlet = new OutletModel(outlet);
                                    const matchedOutlet = this.outletList.find(o => o.outlet.id === item.outlet.id);
                                    if (matchedOutlet) {
                                        const answerEngineAlreadyAdded = matchedOutlet.answerEngines?.some(ae => ae === engineOption.type);
                                        if (!answerEngineAlreadyAdded) {
                                            matchedOutlet.answerEngines.push(engineOption.type);
                                        }
                                    } else {
                                        const outletInfo = {
                                            outlet: item.outlet,
                                            answerEngines: [engineOption.type]
                                        } as ViSourceOutlet;
                                        let circulation: CirculationModel;
                                        if (item.outlet.circulations) {
                                            circulation = item.outlet.circulations.find(c => c.isSimilarWeb);
                                        }
                                        if (circulation) {
                                            outletInfo.circulation = circulation;
                                        }
                                        this.outletList.push(outletInfo);
                                    }
                                } else {
                                    item.outlet = null;
                                }
                                return item;
                            })
                        );
                    })
                );
            }),
            map(sources => {
                return {
                    text: answer,
                    citations: sources?.filter(s => !!s)
                } as ViAnswerResponse;
            })
        ).subscribe({
            next: response => {
                this.outletList = [...this.outletList];
                engineOption.answerText = parseTextToHtml(response.text);
                engineOption.sources = engineOption.answerText ? response.citations : [];
                engineOption.loading = false;

                if (!this.selectedEngineType && engineOption.answerText) {
                    this.selectedEngineType = engineOption.type;
                }
                this.getAnswersEngineResultCheck();
            },
            error: () => {
                engineOption.answerText = '';
                engineOption.sources = [];
                engineOption.loading = false;
                this.getAnswersEngineResultCheck();
            }
        });
    }

    getAnswersEngineResultCheck() {
        if (this.answerEngineOptions.every(answerEngine => !answerEngine.loading)) {
            this.outletListLoading = false;
        }

        const generating = this.answerEngineOptions.some(answerEngine => answerEngine.loading);

        // nothing selected + no answer text → select first item
        const noAnswers = this.answerEngineOptions.every(e => !e.answerText);
        if (noAnswers && !this.selectedEngineType) {
            this.selectedEngineType = this.answerEngineOptions[0].type;
        }

        this.isAnswerGenerating = generating;
    }

    getInTouch(): void {
        this.hubspotFormService.submitForm(HubspotFormTypeEnum.AEO_SCOUT).then(done => {
            this.messageService.add({
                severity: messageSeverityEnum.success,
                summary: 'Common.dialog.aeoScout.upsell.success.summary',
                detail: 'Common.dialog.aeoScout.upsell.success.detail'
            });
        }).catch(err => {
            window.open('https://www.agilitypr.com/contact/');
        });
    }

    learnMore() {
        window.open('https://www.agilitypr.com/visibility-intelligence/');
    }


    copyAnswer(event: Event, answer: string, removeBrTag = false) {
        if (!answer) {
            return;
        }

        const formattedText = htmlToFormattedText(answer, removeBrTag);
        copy.text(formattedText);

        this.messageService.add({
            severity: messageSeverityEnum.success,
            summary: 'Common.dialog.aeoScout.answerCopied.summary',
            detail: 'Common.dialog.aeoScout.answerCopied.detail',
        });

        (event.target as HTMLElement)?.focus();
    }


    private findMatchingOutlet(source: ViAnswerEngineSource): Observable<ViAnswerEngineSource> {
        try {
            const host = new URL(source.link).host;

            // There is bad data in the backend, we want to ignore lookups for some URLs
            if (isBlockedHost(host)) {
                source.outletId = undefined;
                return of(source);
            }

            return this.journodexSearchesApi.searchContactsAndOutlets({
                type: SearchContactsAndOutletsTypeApiEnum.outlet,
                url: host
            }).pipe(
                map(outlets => {
                    source.outletId = outlets[0]?.uuid;
                    return source;
                })
            );
        } catch (e) {
            source.outletId = undefined;
            return of(source);
        }
    }

    private confirmClose(): Observable<boolean> {
        return new Observable<boolean>(observer => {
            const confirmConfig: ConfirmDialogModel = {
                inputs: {
                    id: 'aeoScoutDialog_closeConfirm',
                    heading: 'Common.dialog.aeoScout.closeConfirm.heading',
                    message: 'Common.dialog.aeoScout.closeConfirm.message',
                    bottomMessage: 'Common.dialog.aeoScout.closeConfirm.confirmMessage',
                    bottomMargin: true,
                    okLabel: 'Common.dialog.aeoScout.closeConfirm.continueButton',
                    width: 400
                },
                outputs: {
                    dialogClose: (confirmed: boolean) => {
                        observer.next(!!confirmed);
                        observer.complete();
                    }
                }
            };
            this.coreDialogService.open(ConfirmDialogComponent, confirmConfig);
        });
    }


    private cleanHtml(html: string): string {
        if (!html) {

            return html;
        }
        const blockTags = new Set(['P', 'H1', 'H2', 'H3', 'H4', 'H5', 'H6', 'DIV', 'UL', 'OL', 'LI',
            'BLOCKQUOTE', 'PRE', 'TABLE', 'THEAD', 'TBODY', 'TR', 'TD', 'TH',
            'SECTION', 'ARTICLE', 'HEADER', 'FOOTER', 'NAV', 'ASIDE']);
        const doc = new DOMParser().parseFromString(html, 'text/html');

        const isBlock = (node: Node | null): boolean =>
            node?.nodeType === Node.ELEMENT_NODE && blockTags.has((node as Element).tagName);

        // Skip over whitespace-only text nodes to find the nearest meaningful sibling
        const nearestSibling = (node: Node, forward: boolean): Node | null => {
            let n: Node | null = forward ? node.nextSibling : node.previousSibling;
            while (n?.nodeType === Node.TEXT_NODE && !n.textContent?.trim()) {
                n = forward ? n.nextSibling : n.previousSibling;
            }
            return n;
        };

        for (const br of Array.from(doc.querySelectorAll('br'))) {
            if (isBlock(nearestSibling(br, false)) || isBlock(nearestSibling(br, true))) {
                br.remove();
            }
        }

        return doc.body.innerHTML;
    }

    ngOnDestroy() {
        this.cancel();
        this.subscriptions.forEach(sub => sub.unsubscribe());
    }
}
